import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { ferry, clock, money } from '../lib/ferry'
import { t } from '../lib/i18n'
import Icon, { type IconName } from './Icon'

/**
 * The pass, typed.
 *
 * This was `Record<string, string | number | null>`, which meant
 * `Number(b.total_cents)` compiled whether or not the field existed — and a
 * missing one renders `$NaN` on somebody's ticket. The same index-signature
 * mistake put `NaN` in the operations board's boarded column. On a boarding
 * pass it would be worse: the screen a person is holding at the gate is the one
 * place a nonsense number is least recoverable.
 */
interface Detail {
  booking: {
    id: string
    reference: string
    status: string
    total_cents: number
    route: string
    vessel_name: string
    scheduled_departure: number
    scheduled_arrival: number
    sailing_status: string
  }
  passengers: { name: string; kind: string; needs_priority: number; deck?: string | null; row_label?: string | null; number?: number | null }[]
  extras: { kind: string; quantity: number; plate?: string | null }[]
  pass: { token: string; partySize: number; resident: boolean }
}

/**
 * What a booking status MEANS, in words somebody has a chance of acting on.
 *
 * `held` was rendered raw, and "held" is a database word — it tells you a state
 * machine exists and nothing about whether you are getting on the boat. What
 * somebody needs to know is that they have not paid, that the seats are theirs
 * for now, and that this expires. So each status carries a plain label, a
 * sentence explaining the consequence, and a tone.
 */
const STATUS: Record<string, { label: string; why: string; tone: 'good' | 'warn' | 'bad' | 'muted'; icon: IconName }> = {
  held: { label: 'status.held', why: 'status.heldWhy', tone: 'warn', icon: 'clock' },
  paid: { label: 'status.paid', why: 'status.paidWhy', tone: 'good', icon: 'check' },
  checked_in: { label: 'status.paid', why: 'status.paidWhy', tone: 'good', icon: 'check' },
  boarded: { label: 'status.boarded', why: 'status.boardedWhy', tone: 'good', icon: 'check' },
  cancelled: { label: 'status.cancelled', why: 'status.cancelledWhy', tone: 'bad', icon: 'close' },
  expired: { label: 'status.expired', why: 'status.expiredWhy', tone: 'bad', icon: 'alert' },
  no_show: { label: 'status.expired', why: 'status.expiredWhy', tone: 'muted', icon: 'alert' },
}

const EXTRA_ICON: Record<string, IconName> = {
  bicycle: 'bicycle', pet: 'pet', luggage: 'luggage', vehicle: 'car',
}

/**
 * The boarding pass.
 *
 * ── One code for the whole party ─────────────────────────────────────────────
 *
 * The code encodes the BOOKING, not the passenger, so a family of five is one
 * scan. This is the biggest thing the app does for the queue: five separate
 * passes means five phones, five screens at the wrong brightness, and a parent
 * holding a toddler trying to find the third one.
 *
 * The number under the code is the party size, deliberately large, because the
 * agent's job after scanning is to count heads against it.
 */
export default function Pass({ bookingId, onBack }: { bookingId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<Detail | null>(null)
  const [error, setError] = useState('')
  const canvas = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    ferry.booking(bookingId).then((d) => setDetail(d as unknown as Detail)).catch((e: Error) => setError(e.message))
  }, [bookingId])

  useEffect(() => {
    if (!detail || !canvas.current) return
    void QRCode.toCanvas(canvas.current, detail.pass.token, {
      width: 240, margin: 1,
      // Maximum error correction: a phone screen at a ferry terminal is read in
      // direct Caribbean sun, through a scratched case, often wet.
      errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#ffffff' },
    })
  }, [detail])

  if (error) {
    return <section className="card"><button className="back with-icon" onClick={onBack}><Icon name="back" size={16} />Back</button><p className="error">{error}</p></section>
  }
  if (!detail) return <section className="card"><p className="muted">{t('app.loading')}</p></section>

  const b = detail.booking
  const status = STATUS[b.status] ?? STATUS.held!
  const priority = detail.passengers.some((p) => p.needs_priority)
  const seatOf = (p: Detail['passengers'][number]) =>
    p.row_label && p.number != null ? `${p.row_label}${p.number}` : t('pass.seatAtGate')

  return (
    <section className={`card pass ${detail.pass.resident ? 'pass-resident' : 'pass-visitor'}`}>
      <button className="back with-icon" onClick={onBack}><Icon name="back" size={16} />{t('sail.back')}</button>

      {/*
       * Resident and visitor passes look different at a glance, on purpose. The
       * agent at the ramp checks a hundred of these an hour, and a colour is
       * readable across a metre in a way a word is not.
       */}
      {/*
        * Payment is the `manual` demo provider until Evertec is connected, so
        * this pass was never paid for. Said on the pass itself, above the code:
        * a screenshot of it is the one thing that will travel without the
        * checkout banner that explained it.
        */}
      <div className="demo-banner">
        <Icon name="alert" size={18} />
        <div>
          <strong>{t('pass.previewTitle')}</strong>
          <p>{t('pass.previewBody')}</p>
        </div>
      </div>

      <div className="pass-band">
        <span className="band-who">
          <Icon name={detail.pass.resident ? 'home' : 'person'} size={15} />
          {detail.pass.resident ? t('pass.resident') : t('pass.visitor')}
        </span>
        {priority && <span className="priority-flag"><Icon name="accessible" size={14} />{t('pass.priority')}</span>}
      </div>

      {/*
       * The status, said plainly and with its consequence, ABOVE the code.
       * Somebody whose seats are only held needs to know that before they walk
       * to the terminal — not after an agent tells them at the gate.
       */}
      <div className={`pass-status tone-${status.tone}`}>
        <Icon name={status.icon} size={20} />
        <div>
          <strong>{t(status.label as 'status.held')}</strong>
          <p>{t(status.why as 'status.heldWhy')}</p>
        </div>
      </div>

      <div className="pass-code">
        <canvas ref={canvas} aria-label={t('pass.title')} />
        <p className="party-size">
          <strong>{detail.pass.partySize}</strong>
          <span>{detail.pass.partySize === 1 ? t('pass.traveller') : t('pass.travellers')}</span>
        </p>
      </div>

      {/*
       * A fixed two-column grid rather than auto-fit.
       *
       * `repeat(auto-fit, minmax(140px, 1fr))` reflowed to three columns at some
       * widths and two at others, so the labels landed in a different place on
       * every phone and the block read as ragged. Two columns, always, with the
       * reference spanning both because it is the one people read aloud.
       */}
      <dl className="pass-facts">
        <div className="span-2">
          <dt>{t('pass.reference')}</dt>
          <dd className="reference">{b.reference}</dd>
        </div>
        <div>
          <dt>{t('pass.sailing')}</dt>
          <dd>{clock(b.scheduled_departure)}</dd>
        </div>
        <div>
          <dt>{t('pass.paid')}</dt>
          <dd>{money(b.total_cents / 100)}</dd>
        </div>
      </dl>

      <h3>{t('pass.party')}</h3>
      <ul className="party">
        {detail.passengers.map((p, i) => (
          <li key={i}>
            <span className="who">
              {p.name}
              {p.needs_priority ? <Icon name="accessible" size={14} className="priority-mark" title={t('pass.priority')} /> : null}
            </span>
            <span className="seat-label">{seatOf(p)}</span>
          </li>
        ))}
      </ul>

      {detail.extras.length > 0 && (
        <>
          <h3>{t('pass.with')}</h3>
          <ul className="party">
            {detail.extras.map((x, i) => (
              <li key={i}>
                <span className="who">
                  <Icon name={EXTRA_ICON[x.kind] ?? 'luggage'} size={16} />
                  {x.plate ? x.plate : t(`book.${x.kind}` as 'book.bicycle')}
                </span>
                <span className="seat-label">×{x.quantity}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="muted small">{t('pass.oneScan')}</p>
    </section>
  )
}
